import { Box, Button, Card, Flex, Group, Loader, Table, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrandFacebook,
  IconBrandGoogle,
  IconCheck,
  IconCloudDownload,
  IconDeviceTablet,
  IconMail,
  IconMessage,
  IconRefresh,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useIsSuperAdmin from '../../helpers/useIsSuperAdmin';
import { useLocation, useNavigate, useParams } from 'react-router';
import PageSizeOptions from '../../constants/pageSizeOptions';
import useFetch from '../../helpers/useFetch';
import ConfirmationModal from '../Modals/ConfirmationModal';
import CustomFieldExportModal from '../Modals/CustomFieldExportModal';
import Pager from '../Pager/Pager';
import ProfileRows from '../Profiles/ProfileRows';
import SuperAdminArea from '../SuperAdmin/SuperAdminArea';

const filterDefaultValues = { page: 1, size: PageSizeOptions[0] };

const updateElements = async (fetch, accountId, domainId, segmentRules, segmentId, changed, scope, filter, signal) => {
  return await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments/count`, {
    method: 'POST',
    body: JSON.stringify({
      segment: { rules: segmentRules },
      scope,
      page: filter.page,
      pageSize: filter.size,
      segmentId,
      changed,
    }),
    signal,
  });
};

const SegmentCounter = ({ segment, showSample, scope, size, changed, scopeControl }) => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();

  const parsedSearch = useMemo(() => {
    return queryString.parse(location.search);
  }, [location.search]);

  const [count, setCount] = useState();
  // Initialize from the URL so the first auto-load already targets the right page/size
  // and we don't fire a wasted request that the parsedSearch sync immediately supersedes.
  const [filter, setFilter] = useState(() => ({ ...filterDefaultValues, ...parsedSearch }));
  const [pageCount, setPageCount] = useState();
  const [sample, setSample] = useState();
  const [exportState, setExportState] = useState();
  const [error, setError] = useState();
  const [deleteProfilesStatus, setDeleteProfilesStatus] = useState();
  const [confirmDeleteProfilesOpen, setConfirmDeleteProfilesOpen] = useState(false);
  const [customFieldModalOpen, setCustomFieldModalOpen] = useState(false);
  const [needsEstimation, setNeedsEstimation] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);

  // Track request cancellation and prevent race conditions
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  // Signature of the current rules (values included) plus the page the displayed count
  // was calculated for. Any edit changes the rules signature, which re-shows the
  // "Calculate Segment" button instead of auto-loading or leaving a stale count.
  const rulesSig = useMemo(() => JSON.stringify(segment?.rules ?? []), [segment?.rules]);
  const calculatedRulesSigRef = useRef(null);
  const calculatedFilterKeyRef = useRef(null);
  const calculatedScopeRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const loadedSegmentIdRef = useRef(segment?.id);

  // If this instance is reused for a different segment (re-render instead of remount),
  // reset the load tracking so the new segment's count auto-loads once, as intended.
  if (loadedSegmentIdRef.current !== segment?.id) {
    loadedSegmentIdRef.current = segment?.id;
    hasLoadedRef.current = false;
    calculatedRulesSigRef.current = null;
    calculatedFilterKeyRef.current = null;
    calculatedScopeRef.current = null;
  }

  const isSuperAdmin = useIsSuperAdmin();

  const createExport = (customFields = []) => {
    setExportState('pending');
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments/exports/profiles`, {
      method: 'POST',
      body: JSON.stringify({ segment, customFields }),
    })
      .then(() => {
        setExportState('success');
      })
      .catch(() => {
        setExportState('error');
      });
  };

  const deleteProfiles = (segment) => {
    setConfirmDeleteProfilesOpen(false);
    setDeleteProfilesStatus('pending');
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments/deleteProfiles`, {
      method: 'POST',
      body: JSON.stringify({ segment }),
    })
      .then(() => {
        setDeleteProfilesStatus('success');
      })
      .catch(() => {
        setDeleteProfilesStatus('error');
      });
  };

  const handleEstimate = useCallback(() => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Increment request ID to track latest request
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    setIsEstimating(true);
    setNeedsEstimation(false);

    // Remember what this calculation covers, so later edits/paging are detected.
    // Reuse the memoized rulesSig so the stored snapshot can't diverge from the value
    // the effect compares against.
    hasLoadedRef.current = true;
    calculatedRulesSigRef.current = rulesSig;
    calculatedFilterKeyRef.current = `${filter.page}|${filter.size}`;
    calculatedScopeRef.current = scope;

    updateElements(
      fetch,
      accountId,
      domainId,
      segment.rules,
      segment?.id,
      changed,
      scope,
      filter,
      abortController.signal
    )
      .then(({ count, sample }) => {
        // Only update state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setCount(count);
          setPageCount(Math.ceil(count.total / filter.size));
          setSample(sample);
        }
      })
      .catch((error) => {
        // Ignore abort errors, only handle real errors for the latest request
        if (error.name !== 'AbortError' && currentRequestId === requestIdRef.current) {
          setError(error);
        }
      })
      .finally(() => {
        // Only update loading state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setIsEstimating(false);
        }
      });
  }, [accountId, changed, domainId, fetch, filter, scope, segment?.id, segment?.rules, rulesSig]);

  useEffect(() => {
    const newFilter = { ...filterDefaultValues, ...parsedSearch };
    setFilter(newFilter);
  }, [parsedSearch]);

  useEffect(() => {
    if (segment?.scope !== 'behavior') {
      return;
    }

    // Inline preview always auto-loads.
    if (size === 'sm') {
      handleEstimate();
      return;
    }

    // First time the segment is shown → present its count automatically.
    if (!hasLoadedRef.current) {
      handleEstimate();
      return;
    }

    // Any edit to the rules (including a value inside a filter) → require an explicit
    // "Calculate Segment" click instead of auto-recalculating. Clearing the count first
    // guarantees we never show the count and the button at the same time.
    if (rulesSig !== calculatedRulesSigRef.current) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Bump the request id so any in-flight response is dropped by the id guard
      // even if it resolves before the abort takes effect.
      requestIdRef.current += 1;
      setCount(undefined);
      setPageCount(undefined);
      setSample(undefined);
      setNeedsEstimation(true);
      setIsEstimating(false);
      return;
    }

    if (scope !== calculatedScopeRef.current) {
      setCount(undefined);
      setPageCount(undefined);
      setSample(undefined);
      handleEstimate();
      return;
    }

    // Paging / page-size change while a count is shown → refetch that page.
    if (`${filter.page}|${filter.size}` !== calculatedFilterKeyRef.current) {
      handleEstimate();
    }
  }, [handleEstimate, segment?.scope, scope, size, rulesSig, filter.page, filter.size]);

  // Cleanup: cancel any in-flight requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (segment === undefined || segment.scope !== 'behavior') {
    return null;
  }

  const isSmall = size === 'sm';
  const textSize = isSmall ? 'sm' : 'lg';
  const iconSize = isSmall ? 14 : 16;

  let counterElement;
  if (isEstimating) {
    counterElement = (
      <Group gap={4} align="center">
        <Loader size={14} /> <Text size="sm">Calculating...</Text>
      </Group>
    );
  } else if (needsEstimation || !count) {
    counterElement = (
      <Group justify="center">
        <Button size="lg" onClick={handleEstimate} data-tour="segmentCalculateBtn">
          <IconRefresh size={16} /> Calculate Segment
        </Button>
      </Group>
    );
  } else if (count) {
    counterElement = (
      <Group gap={isSmall ? 4 : 'xs'} wrap="nowrap">
        <Group gap={2} wrap="nowrap">
          <IconUser size={iconSize} />
          <Text size={textSize}>{count.total.toLocaleString(navigator.language)} Profiles (</Text>
        </Group>
        <Group gap={2} wrap="nowrap">
          <IconMail size={iconSize} />
          <Text size={textSize}>{count.canEmail.toLocaleString(navigator.language)}</Text>
        </Group>
        <Text c="dimmed">|</Text>
        <Group gap={2} wrap="nowrap">
          <IconMessage size={iconSize} />
          <Text size={textSize}>{count.canText.toLocaleString(navigator.language)}</Text>
        </Group>
        <Text c="dimmed">|</Text>
        <Group gap={2} wrap="nowrap">
          <IconDeviceTablet size={iconSize} />
          <Text size={textSize}>{count.canPush.toLocaleString(navigator.language)}</Text>
        </Group>
        <Text c="dimmed">|</Text>
        <Group gap={2} wrap="nowrap">
          <IconBrandFacebook size={iconSize} />
          <Text size={textSize}>{count.canBeFacebookTargeted.toLocaleString(navigator.language)}</Text>
        </Group>
        <Text c="dimmed">|</Text>
        <Group gap={2} wrap="nowrap">
          <IconBrandGoogle size={iconSize} />
          <Text size={textSize}>{count.canBeGoogleTargeted.toLocaleString(navigator.language)})</Text>
        </Group>
      </Group>
    );
  }

  if (size === 'sm') {
    return <Box c="dimmed">{counterElement}</Box>;
  }

  // No wrapper needed - counterElement is already properly formatted

  let exportButton;
  let deleteProfilesButton;
  const availableSample = showSample && sample && sample.length > 0;

  if (availableSample) {
    exportButton = (
      <Button
        variant="default"
        onClick={() => setCustomFieldModalOpen(true)}
        disabled={!exportState === 'pending' || !exportState === 'error'}
        data-tour="segmentExportBtn"
      >
        <IconCloudDownload size={16} /> Export Segment
      </Button>
    );

    if (exportState === 'success') {
      exportButton = (
        <Button variant="default" disabled={true}>
          <IconCheck size={16} /> Export scheduled. You will receive an email with a download link when it&apos;s ready.
        </Button>
      );
    }

    if (exportState === 'error') {
      exportButton = (
        <Button color="red" disabled={true}>
          <IconAlertTriangle size={16} /> An error occurred while scheduling export.
        </Button>
      );
    }

    deleteProfilesButton = (
      <Button
        color="red"
        disabled={!deleteProfilesStatus === 'pending' || !deleteProfilesStatus === 'error'}
        onClick={() => setConfirmDeleteProfilesOpen(true)}
      >
        <IconTrash size={16} /> Delete profiles
      </Button>
    );

    if (deleteProfilesStatus === 'success') {
      deleteProfilesButton = (
        <Button variant="default" disabled={true} onClick={() => setConfirmDeleteProfilesOpen(true)}>
          <IconCheck size={16} /> Profiles scheduled for deletion. Removal in progress.
        </Button>
      );
    }

    if (deleteProfilesStatus === 'error') {
      deleteProfilesButton = (
        <Button color="red" disabled={true}>
          <IconAlertTriangle size={16} /> An error occurred while scheduling profiles for deletion.
        </Button>
      );
    }
  }

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    if (selectedPage !== filter.page) {
      navigate({ search: queryString.stringify({ ...filter, page: selectedPage }) });
    }
  };

  const handlePageSizeClick = (size) => {
    navigate({ search: queryString.stringify({ page: 1, size: size }) });
  };

  return (
    <>
      <ConfirmationModal
        error={error}
        show={confirmDeleteProfilesOpen}
        close={() => setConfirmDeleteProfilesOpen(false)}
        confirm={() => {
          deleteProfiles(segment);
        }}
        message="Deleting profiles is irreversible!  Are you sure?"
      />
      <CustomFieldExportModal
        show={customFieldModalOpen}
        close={() => setCustomFieldModalOpen(false)}
        segment={segment}
        onExport={createExport}
      />
      <Card withBorder>
        <Flex justify="space-between" align="center" wrap="wrap" gap="md" data-tour="segmentEstimatedProfiles">
          <Box style={{ flex: needsEstimation || isEstimating || !count ? 1 : undefined }}>
            <Group
              gap="md"
              wrap="wrap"
              justify={!isEstimating && (needsEstimation || !count) ? 'center' : 'flex-start'}
            >
              {counterElement}
              {scopeControl}
            </Group>
          </Box>
          {!needsEstimation && !isEstimating && (
            <Group gap="sm">
              {exportButton}
              {deleteProfilesButton && (
                <SuperAdminArea show={isSuperAdmin} inline>
                  {deleteProfilesButton}
                </SuperAdminArea>
              )}
            </Group>
          )}
        </Flex>
        {!needsEstimation && !isEstimating && showSample ? (
          <>
            {sample === undefined || sample.length === 0 ? null : (
              <Table mt="md" striped withTableBorder withColumnBorders highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Profile</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Phone number</Table.Th>
                    <Table.Th>Registered At</Table.Th>
                    <Table.Th>Subscribed At</Table.Th>
                    <Table.Th>Last Change At</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <ProfileRows profiles={sample} />
                </Table.Tbody>
              </Table>
            )}

            <Box style={{ float: 'right', padding: '1rem' }}>
              <Pager
                pageSize={parseInt(filter.size)}
                pageCount={pageCount}
                onPageChange={handlePageClick.bind(this)}
                initialPage={parseInt(filter.page)}
                setPageSize={handlePageSizeClick}
              />
            </Box>
          </>
        ) : null}
      </Card>
    </>
  );
};

export default SegmentCounter;
